import { useState, useEffect, useCallback } from 'react'
import { X, Loader2, Clock, User, ChevronDown } from 'lucide-react'
import { scopesApi } from '@api/client'

interface LogEntry {
  id: number
  user_email: string | null
  user_name: string | null
  section: string
  field_path: string
  old_value: string
  new_value: string
  created_at: string
}

interface Props {
  dealId: number
  onClose: () => void
}

/** Pretty-print a field path like "amlStandard" → "Aml Standard" */
function formatFieldPath(path: string): string {
  return path
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]/g, ' ')
    .replace(/^\s/, '')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim()
}

/** Try to display a JSON value nicely */
function formatValue(raw: string): string {
  if (!raw || raw === '""' || raw === 'null') return '(empty)'
  try {
    const parsed = JSON.parse(raw)
    if (typeof parsed === 'boolean') return parsed ? 'Yes' : 'No'
    if (typeof parsed === 'string') return parsed || '(empty)'
    if (Array.isArray(parsed)) {
      if (parsed.length === 0) return '(none)'
      return parsed.join(', ')
    }
    if (typeof parsed === 'object' && parsed !== null) {
      // For objects, show a compact summary
      const entries = Object.entries(parsed).filter(([, v]) => v !== null && v !== '' && v !== false)
      if (entries.length === 0) return '(empty)'
      if (entries.length <= 3) return entries.map(([k, v]) => `${formatFieldPath(k)}: ${v}`).join(', ')
      return `${entries.length} fields`
    }
    return String(parsed)
  } catch {
    return raw
  }
}

/** Group entries by timestamp (to the minute) and user */
interface LogGroup {
  timestamp: string
  user_name: string | null
  user_email: string | null
  entries: LogEntry[]
}

function groupEntries(entries: LogEntry[]): LogGroup[] {
  const groups: LogGroup[] = []
  for (const entry of entries) {
    const ts = entry.created_at.slice(0, 16) // group by YYYY-MM-DD HH:MM
    const last = groups[groups.length - 1]
    if (last && last.timestamp === ts && last.user_email === entry.user_email) {
      last.entries.push(entry)
    } else {
      groups.push({
        timestamp: ts,
        user_name: entry.user_name,
        user_email: entry.user_email,
        entries: [entry],
      })
    }
  }
  return groups
}

function formatTimestamp(ts: string): string {
  try {
    const d = new Date(ts + 'Z')
    return d.toLocaleString('en-AU', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })
  } catch {
    return ts
  }
}

export function ScopeLogModal({ dealId, onClose }: Props) {
  const [entries, setEntries] = useState<LogEntry[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set())
  const [sectionFilter, setSectionFilter] = useState<string>('all')

  const load = useCallback(async (offset = 0) => {
    const isMore = offset > 0
    if (isMore) setLoadingMore(true)
    else setLoading(true)
    try {
      const res = await scopesApi.getLog(dealId, 200, offset)
      if (res.success && res.data) {
        const fetched = Array.isArray(res.data.entries) ? res.data.entries : []
        setEntries((prev) => (offset === 0 ? fetched : [...prev, ...fetched]))
        setTotal(res.data.total)
      }
    } catch {
      // silent
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }, [dealId])

  useEffect(() => { load() }, [load])

  const toggleGroup = (idx: number) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(idx)) next.delete(idx)
      else next.add(idx)
      return next
    })
  }

  const filtered = sectionFilter === 'all' ? entries : entries.filter(e => e.section === sectionFilter)
  const groups = groupEntries(filtered)
  const sections = [...new Set(entries.map(e => e.section))].sort()

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-xl shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border-color)]">
          <div>
            <h3 className="text-base font-semibold text-[var(--text-primary)]">Scope Change Log</h3>
            <p className="text-xs text-[var(--text-muted)] mt-0.5">
              {total} change{total !== 1 ? 's' : ''} recorded
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[var(--bg-tertiary)] rounded-lg transition-colors">
            <X size={18} className="text-[var(--text-muted)]" />
          </button>
        </div>

        {/* Filter */}
        {sections.length > 1 && (
          <div className="px-5 py-2 border-b border-[var(--border-color)] flex items-center gap-2">
            <span className="text-xs text-[var(--text-muted)]">Filter:</span>
            <button
              className={`px-2 py-1 text-xs rounded-full border transition-colors ${sectionFilter === 'all' ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-[var(--border-color)] text-[var(--text-secondary)] hover:border-gray-500'}`}
              onClick={() => setSectionFilter('all')}
            >
              All
            </button>
            {sections.map((s) => (
              <button
                key={s}
                className={`px-2 py-1 text-xs rounded-full border transition-colors ${sectionFilter === s ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-[var(--border-color)] text-[var(--text-secondary)] hover:border-gray-500'}`}
                onClick={() => setSectionFilter(s)}
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={20} className="animate-spin text-[var(--text-muted)]" />
            </div>
          ) : groups.length === 0 ? (
            <div className="text-center py-12">
              <Clock size={32} className="mx-auto text-[var(--text-muted)] mb-3" />
              <p className="text-sm text-[var(--text-muted)]">No changes recorded yet.</p>
              <p className="text-xs text-[var(--text-muted)] mt-1">Changes will appear here as users edit scope fields.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {groups.map((group, idx) => {
                const expanded = expandedGroups.has(idx)
                return (
                  <div key={idx} className="border border-[var(--border-color)] rounded-lg overflow-hidden">
                    <button
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[var(--bg-secondary)] transition-colors text-left"
                      onClick={() => toggleGroup(idx)}
                    >
                      <ChevronDown size={14} className={`text-[var(--text-muted)] transition-transform ${expanded ? 'rotate-180' : ''}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <User size={12} className="text-[var(--text-muted)]" />
                          <span className="text-sm font-medium text-[var(--text-primary)] truncate">
                            {group.user_name || group.user_email || 'Unknown user'}
                          </span>
                          <span className="text-[10px] text-[var(--text-muted)]">
                            {formatTimestamp(group.timestamp)}
                          </span>
                        </div>
                        <p className="text-xs text-[var(--text-muted)] mt-0.5">
                          {[...new Set(group.entries.map(e => e.section))].join(', ')}
                          {' — '}
                          {group.entries.length <= 5
                            ? group.entries.map(e => formatFieldPath(e.field_path)).join(', ')
                            : group.entries.slice(0, 4).map(e => formatFieldPath(e.field_path)).join(', ') + ` +${group.entries.length - 4} more`
                          }
                        </p>
                      </div>
                    </button>
                    {expanded && (
                      <div className="border-t border-[var(--border-color)]">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="bg-[var(--bg-secondary)]">
                              <th className="text-left px-4 py-2 text-[var(--text-muted)] font-medium w-24">Section</th>
                              <th className="text-left px-4 py-2 text-[var(--text-muted)] font-medium w-36">Field</th>
                              <th className="text-left px-4 py-2 text-[var(--text-muted)] font-medium">From</th>
                              <th className="text-left px-4 py-2 text-[var(--text-muted)] font-medium">To</th>
                            </tr>
                          </thead>
                          <tbody>
                            {group.entries.map((entry) => (
                              <tr key={entry.id} className="border-t border-[var(--border-color)]">
                                <td className="px-4 py-2 text-[var(--text-secondary)]">{entry.section}</td>
                                <td className="px-4 py-2 text-[var(--text-primary)] font-medium">{formatFieldPath(entry.field_path)}</td>
                                <td className="px-4 py-2 text-red-400 max-w-[200px] truncate" title={formatValue(entry.old_value)}>
                                  {formatValue(entry.old_value)}
                                </td>
                                <td className="px-4 py-2 text-green-400 max-w-[200px] truncate" title={formatValue(entry.new_value)}>
                                  {formatValue(entry.new_value)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )
              })}

              {entries.length < total && (
                <button
                  className="w-full py-2 text-xs text-primary hover:underline disabled:opacity-50"
                  onClick={() => load(entries.length)}
                  disabled={loadingMore}
                >
                  {loadingMore ? 'Loading...' : `Load more (${entries.length} of ${total})`}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
